import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Button, 
  Form, Alert, Spinner, Image, Modal,
  Table, Badge, ButtonGroup
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from './components/AdminNavbar';
import { authAPI, BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const API_BASE_URL = BASE_URL;

const AdminHomeSlider = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    button_text: 'Learn More',
    button_link: '/',
    display_order: 0,
    is_active: 1
  });
  
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  // API Helper
  const apiFetch = async (endpoint, method = 'GET', data = null) => {
    const token = localStorage.getItem('admin_token') || '';
    
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const config = {
      method,
      headers,
    };
    
    if (data && method !== 'GET') {
      config.body = JSON.stringify(data);
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  };

  // Upload image
  const uploadImageDirect = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await fetch(`${API_BASE_URL}/slider.php?action=uploadImage`, {
        method: 'POST',
        body: formData,
      });
      
      return await response.json();
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  // API functions
  const slideAPI = {
    getAll: async () => {
      return await apiFetch('/slider.php?action=getAll');
    },
    
    create: async (data) => {
      return await apiFetch('/slider.php?action=create', 'POST', data);
    },
    
    update: async (id, data) => {
      return await apiFetch(`/slider.php?action=update&id=${id}`, 'POST', data);
    },
    
    delete: async (id) => {
      return await apiFetch(`/slider.php?action=delete&id=${id}`, 'DELETE');
    },
    
    toggleStatus: async (id) => {
      return await apiFetch(`/slider.php?action=toggleStatus&id=${id}`, 'POST');
    },
    
    reorder: async (slides) => {
      return await apiFetch('/slider.php?action=reorder', 'POST', { slides });
    },
    
    uploadImage: uploadImageDirect
  };

  useEffect(() => {
    const currentUser = authAPI.getCurrentUser();
    if (!currentUser) {
      navigate('/admin/login');
      return;
    }
    setUser(currentUser);
    fetchSlides();
  }, [navigate]);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await slideAPI.getAll();
      
      if (result.success) {
        // Ensure all is_active values are numbers, not strings
        const formattedSlides = result.data.map(slide => ({
          ...slide,
          id: parseInt(slide.id),
          display_order: parseInt(slide.display_order),
          is_active: parseInt(slide.is_active)
        }));
        setSlides(formattedSlides || []);
      } else {
        setError(result.message || 'Failed to load slides');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Server error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.image_url.trim()) {
      errors.image_url = 'Image is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      let result;
      if (modalMode === 'add') {
        result = await slideAPI.create(formData);
      } else {
        result = await slideAPI.update(currentSlide.id, formData);
      }
      
      if (result.success) {
        setSuccess(`Slide ${modalMode === 'add' ? 'created' : 'updated'} successfully`);
        setShowModal(false);
        fetchSlides();
      } else {
        setError(result.message || 'Operation failed');
      }
    } catch (error) {
      setError('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleAddSlide = () => {
    setFormData({
      title: '',
      subtitle: '',
      image_url: '',
      button_text: 'Learn More',
      button_link: '/',
      display_order: slides.length + 1,
      is_active: 1
    });
    setFormErrors({});
    setModalMode('add');
    setCurrentSlide(null);
    setShowModal(true);
  };

  const handleEditSlide = (slide) => {
    setFormData({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      image_url: slide.image_url || '',
      button_text: slide.button_text || 'Learn More',
      button_link: slide.button_link || '/',
      display_order: slide.display_order || 0,
      is_active: slide.is_active || 1
    });
    setFormErrors({});
    setModalMode('edit');
    setCurrentSlide(slide);
    setShowModal(true);
  };

  const handleDeleteClick = (slide) => {
    setCurrentSlide(slide);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentSlide) return;
    
    try {
      setLoading(true);
      const result = await slideAPI.delete(currentSlide.id);
      
      if (result.success) {
        setSuccess('Slide deleted successfully');
        setShowDeleteModal(false);
        fetchSlides();
      } else {
        setError(result.message || 'Delete failed');
      }
    } catch (error) {
      setError('Error: ' + error.message);
    } finally {
      setLoading(false);
      setCurrentSlide(null);
    }
  };

  const handleImageUpload = () => {
    setShowImageModal(true);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      
      const result = await slideAPI.uploadImage(file);
      
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          image_url: result.data.image_path
        }));
        setSuccess('Image uploaded successfully!');
        setShowImageModal(false);
      } else {
        setError(result.message || 'Upload failed');
      }
    } catch (error) {
      setError('Upload error: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleToggleStatus = async (slide) => {
    try {
      const result = await slideAPI.toggleStatus(slide.id);
      
      if (result.success) {
        const newStatus = result.data.is_active;
        setSuccess(`Slide ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        
        // Update local state immediately
        setSlides(prevSlides => 
          prevSlides.map(s => 
            s.id === slide.id ? { ...s, is_active: newStatus } : s
          )
        );
      } else {
        setError(result.message || 'Failed to update status');
        fetchSlides(); // Refresh on error
      }
    } catch (error) {
      setError('Error: ' + error.message);
      fetchSlides(); // Refresh on error
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    
    const updatedSlides = [...slides];
    const temp = updatedSlides[index];
    updatedSlides[index] = updatedSlides[index - 1];
    updatedSlides[index - 1] = temp;
    
    // Update order numbers
    const reorderedSlides = updatedSlides.map((slide, idx) => ({
      ...slide,
      display_order: idx + 1
    }));
    
    setSlides(reorderedSlides);
    
    try {
      const result = await slideAPI.reorder(reorderedSlides.map(s => ({
        id: s.id,
        display_order: s.display_order
      })));
      
      if (!result.success) {
        setError('Failed to save order');
        fetchSlides(); // Revert
      }
    } catch (error) {
      setError('Reorder error: ' + error.message);
      fetchSlides(); // Revert
    }
  };

  const handleMoveDown = async (index) => {
    if (index === slides.length - 1) return;
    
    const updatedSlides = [...slides];
    const temp = updatedSlides[index];
    updatedSlides[index] = updatedSlides[index + 1];
    updatedSlides[index + 1] = temp;
    
    // Update order numbers
    const reorderedSlides = updatedSlides.map((slide, idx) => ({
      ...slide,
      display_order: idx + 1
    }));
    
    setSlides(reorderedSlides);
    
    try {
      const result = await slideAPI.reorder(reorderedSlides.map(s => ({
        id: s.id,
        display_order: s.display_order
      })));
      
      if (!result.success) {
        setError('Failed to save order');
        fetchSlides(); // Revert
      }
    } catch (error) {
      setError('Reorder error: ' + error.message);
      fetchSlides(); // Revert
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Helper function to parse status correctly
  const parseStatus = (status) => {
    if (status === true || status === false) {
      return status ? 1 : 0;
    }
    if (typeof status === 'string') {
      return status === '1' ? 1 : 0;
    }
    return status === 1 ? 1 : 0;
  };

  if (!user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminNavbar onLogout={handleLogout} user={user} />
      
      <Container fluid className="py-4 admin-content">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="fw-bold mb-0">
              <i className="fas fa-images me-2 text-primary"></i>
              Home Slider Management
            </h2>
            <p className="text-muted mb-0">Manage homepage slider content</p>
          </Col>
          <Col className="text-end">
            <Button 
              variant="outline-secondary" 
              onClick={fetchSlides}
              disabled={loading}
              className="me-2"
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </Button>
            <Button 
              variant="primary"
              onClick={handleAddSlide}
              disabled={loading}
            >
              <i className="fas fa-plus me-2"></i>
              Add New Slide
            </Button>
          </Col>
        </Row>

        {/* Messages */}
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {/* Stats */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow border-primary">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Total Slides</h6>
                    <h3 className="mb-0">{slides.length}</h3>
                  </div>
                  <i className="fas fa-images fa-2x text-primary"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow border-success">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Active Slides</h6>
                    <h3 className="mb-0">
                      {slides.filter(s => parseStatus(s.is_active) === 1).length}
                    </h3>
                  </div>
                  <i className="fas fa-eye fa-2x text-success"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow border-danger">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Inactive Slides</h6>
                    <h3 className="mb-0">
                      {slides.filter(s => parseStatus(s.is_active) === 0).length}
                    </h3>
                  </div>
                  <i className="fas fa-eye-slash fa-2x text-danger"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow border-warning">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Preview</h6>
                    <Button 
                      variant="link" 
                      className="p-0 text-warning"
                      onClick={() => window.open('/', '_blank')}
                    >
                      View Homepage <i className="fas fa-external-link-alt ms-1"></i>
                    </Button>
                  </div>
                  <i className="fas fa-desktop fa-2x text-warning"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading slides...</p>
          </div>
        ) : (
          <Card className="shadow">
            <Card.Header className="bg-white border-bottom-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Slides List ({slides.length} total)
                </h5>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {slides.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-images fa-3x text-muted mb-3"></i>
                  <h5>No slides found</h5>
                  <p className="text-muted mb-3">Create your first slide to get started</p>
                  <Button variant="primary" onClick={handleAddSlide}>
                    <i className="fas fa-plus me-2"></i>
                    Add First Slide
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: '150px' }}>Order Control</th>
                        <th style={{ width: '100px' }}>Image</th>
                        <th>Content</th>
                        <th style={{ width: '120px' }}>Status</th>
                        <th style={{ width: '120px' }}>Created</th>
                        <th style={{ width: '180px' }} className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slides.map((slide, index) => {
                        const isActive = parseStatus(slide.is_active) === 1;
                        return (
                          <tr key={slide.id} className={!isActive ? 'table-secondary' : ''}>
                            <td>
                              <div className="d-flex align-items-center">
                                <ButtonGroup size="sm" className="me-2">
                                  <Button
                                    variant={!isActive ? "outline-secondary" : "outline-primary"}
                                    onClick={() => handleMoveUp(index)}
                                    disabled={index === 0}
                                    title="Move Up"
                                  >
                                    <i className="fas fa-arrow-up"></i>
                                  </Button>
                                  <Button
                                    variant={!isActive ? "outline-secondary" : "outline-primary"}
                                    onClick={() => handleMoveDown(index)}
                                    disabled={index === slides.length - 1}
                                    title="Move Down"
                                  >
                                    <i className="fas fa-arrow-down"></i>
                                  </Button>
                                </ButtonGroup>
                                <span className="fw-bold">#{slide.display_order}</span>
                              </div>
                            </td>
                            <td>
                              {slide.image_url ? (
                                <div 
                                  className="border rounded overflow-hidden"
                                  style={{ width: '80px', height: '60px' }}
                                >
                                  <Image
                                    src={slide.image_url.startsWith('http') ? slide.image_url : `${UPLOAD_BASE_URL}/${slide.image_url}`}
                                    alt={slide.title}
                                    fluid
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/800x600?text=Image+Error';
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="bg-light d-flex align-items-center justify-content-center border rounded"
                                  style={{ width: '80px', height: '60px' }}>
                                  <i className="fas fa-image text-muted"></i>
                                </div>
                              )}
                            </td>
                            <td>
                              <div>
                                <strong className="d-block mb-1">{slide.title}</strong>
                                {slide.subtitle && (
                                  <p className="text-muted small mb-1">{slide.subtitle}</p>
                                )}
                                {(slide.button_text || slide.button_link) && (
                                  <small className="text-info">
                                    <i className="fas fa-link me-1"></i>
                                    {slide.button_text} → {slide.button_link}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Form.Check
                                  type="switch"
                                  id={`status-switch-${slide.id}`}
                                  checked={isActive}
                                  onChange={() => handleToggleStatus(slide)}
                                  label={
                                    <Badge bg={isActive ? "success" : "secondary"} className="ms-2">
                                      {isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              <small className="text-muted">
                                {formatDate(slide.created_at)}
                              </small>
                            </td>
                            <td className="text-center">
                              <ButtonGroup size="sm">
                                <Button
                                  variant="outline-primary"
                                  onClick={() => handleEditSlide(slide)}
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  onClick={() => handleDeleteClick(slide)}
                                  title="Delete"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </ButtonGroup>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-images me-2"></i>
              {modalMode === 'add' ? 'Add New Slide' : 'Edit Slide'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.title}
                    placeholder="Enter slide title"
                    disabled={loading}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.title}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subtitle</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    placeholder="Optional subtitle"
                    disabled={loading}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Button Text</Form.Label>
                      <Form.Control
                        type="text"
                        name="button_text"
                        value={formData.button_text}
                        onChange={handleInputChange}
                        placeholder="Learn More"
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Button Link</Form.Label>
                      <Form.Control
                        type="text"
                        name="button_link"
                        value={formData.button_link}
                        onChange={handleInputChange}
                        placeholder="/"
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleInputChange}
                    min="1"
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    Lower numbers appear first
                  </Form.Text>
                </Form.Group>

                <Form.Check
                  type="switch"
                  id="is_active"
                  name="is_active"
                  label="Active (Show on homepage)"
                  checked={parseStatus(formData.is_active) === 1}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      is_active: e.target.checked ? 1 : 0
                    }));
                  }}
                  className="mb-3"
                  disabled={loading}
                />
              </Col>

              <Col md={4}>
                <Card className="h-100">
                  <Card.Header>
                    <h6 className="mb-0">
                      <i className="fas fa-image me-2"></i>
                      Slide Image <span className="text-danger">*</span>
                    </h6>
                  </Card.Header>
                  <Card.Body className="text-center">
                    {formData.image_url ? (
                      <div className="mb-3">
                        <Image
                          src={formData.image_url.startsWith('http') ? formData.image_url : `${UPLOAD_BASE_URL}/${formData.image_url}`}
                          alt="Preview"
                          fluid
                          className="border rounded"
                          style={{ 
                            maxHeight: '150px', 
                            width: '100%', 
                            objectFit: 'cover' 
                          }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                          }}
                        />
                        <p className="small text-muted mt-2">
                          Current Image
                        </p>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <i className="fas fa-image fa-3x text-muted mb-2"></i>
                        <p className="text-muted">No image selected</p>
                      </div>
                    )}
                    
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.image_url}
                        placeholder="Image URL"
                        disabled={loading}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.image_url}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Button 
                      variant="outline-primary" 
                      onClick={handleImageUpload}
                      className="w-100 mb-2"
                      disabled={loading}
                    >
                      <i className="fas fa-upload me-2"></i>
                      Upload Image
                    </Button>
                    
                    <Form.Text className="text-muted d-block">
                      <small>Recommended: 1920×800 pixels</small>
                      <br />
                      <small>Max size: 5MB</small>
                      <br />
                      <small>Or use placeholder URL</small>
                    </Form.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  {modalMode === 'add' ? 'Add Slide' : 'Save Changes'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <i className="fas fa-trash-alt fa-3x text-danger mb-3"></i>
            <h5>Are you sure?</h5>
            <p className="text-muted">
              You are about to delete the slide titled:<br />
              <strong>"{currentSlide?.title}"</strong>
            </p>
            {currentSlide?.image_url && (
              <p className="text-muted small">
                <i className="fas fa-image me-1"></i>
                The associated image will also be deleted.
              </p>
            )}
            <p className="text-danger small">
              <i className="fas fa-exclamation-circle me-1"></i>
              This action cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Slide'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Upload Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-upload me-2"></i>
            Upload Slide Image
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <i className="fas fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
            <p className="mb-3">
              Select an image file (JPG, PNG, GIF, WebP)
              <br />
              <small className="text-muted">Recommended: 1920×800 pixels</small>
              <br />
              <small className="text-muted">Max size: 5MB</small>
            </p>
            <Form.Group>
              <Form.Control
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handleImageSelect}
                disabled={uploading}
              />
            </Form.Group>
            {uploading && (
              <div className="mt-3">
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)} disabled={uploading}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminHomeSlider;